import { AnalyticsService } from 'base/analytics';
import { UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { Button, ButtonPalette, RadioButtonGroup } from 'base/ui/button';
import {
  IconAdd,
  IconChevronRight,
  IconClear,
  IconSearch,
} from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { Rows } from 'base/ui/layout';
import { DEFAULT_LOCALE } from 'base/ui/locales';
import { TextSmall, TextSmallLight, TextTinyLight } from 'base/ui/typography';
import { canAddToSlot, findSlotsIn } from 'blocks/component_renderer/slots';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Assistant } from '../ai/assistant';
import { getGenerateCopyError } from '../ai/validation';
import { globalSectionsInProject } from '../editing_actions/global_sections';
import {
  aReferenceSection,
  ElementInstanceState,
  SectionRefState,
} from '../model';
import { parentElementWithinRoot, unrefSection } from '../model_helpers';
import { SelectionStore } from '../selection/store';
import { AddRoute, SidebarNavigation } from '../sidebar/routes';
import { sectionDndDomain } from '../sitemap/section_dnd';
import { commitSuggestion, Suggestion } from '../suggested_edits';
import globalSectionImage from './category_images/global.file.svg';
import { fuzzyTextSearch } from './fuzzy_text_search';
import pageCategoriesImage from './page_categories.file.svg';
import { RemoveSingleSuggestion, SingleSuggestionStore } from './suggestions';
import styles from './ui.css';

export function CardButtonContent({
  label,
  locale,
  description,
  image,
  icon: Icon = IconAdd,
}: {
  label: string;
  locale?: string;
  description?: string;
  image: string;
  icon?: React.ComponentType;
}) {
  return (
    <div className={styles.cardButton} lang={locale}>
      <img src={image} className={styles.cardButtonImage} />
      <Rows spacing={0}>
        <TextSmall lineClamp={1}>{label}</TextSmall>
        <TextTinyLight lineClamp={1}>{description}</TextTinyLight>
      </Rows>
      <div className={styles.cardButtonIcon}>
        <Icon />
      </div>
    </div>
  );
}

export function AddSectionButton({
  children,
  section,
  selection,
  suggestionStore,
  analytics,
  library,
  size = 'large',
}: {
  children: React.ReactNode;
  section: SectionRefState;
  selection: SelectionStore;
  suggestionStore: SingleSuggestionStore;
  analytics: AnalyticsService;
  library: LibraryCache;
  size?: 'medium' | 'large';
}) {
  const removeMySuggestion = React.useRef<RemoveSingleSuggestion>();
  const sel = selection.get();
  const suggestion = {
    type: 'add-sections',
    page:
      sel.page ||
      (sel.space && sel.space !== 'home' ? sel.space.subPages[0] : undefined) ||
      selection.project.homePage,
    index: sel.section ? sel.page.sections.indexOf(sel.section) + 1 : 'end',
    sections: [section],
  } satisfies Suggestion;
  const sectionValue = unrefSection(section, selection.project.state);

  const onClick = () => {
    suggestionStore.clear();
    commitSuggestion(suggestion, selection, library);
    trackAdded();
  };
  const trackAdded = () => {
    analytics.track({
      event: 'blocks/add-section',
      method: 'result',
      name: sectionValue?.name,
      isGlobal: section.type === 'reference',
      interaction: 'add',
    });
  };

  const onMouseEnter = () => {
    removeMySuggestion.current = suggestionStore.set(suggestion);
  };
  const onMouseLeave = () => {
    // a delay to reduce flickering when mousing between buttons
    removeMySuggestion.current?.({ delayMs: 100 });
  };
  React.useEffect(() => () => removeMySuggestion.current?.(), []);

  return (
    <sectionDndDomain.Draggable
      source={{ section }}
      onDragStart={onMouseLeave}
      onAfterDrop={trackAdded}
    >
      <Button
        onClick={onClick}
        fullWidth
        variant="secondary"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        size={size}
      >
        {children}
      </Button>
    </sectionDndDomain.Draggable>
  );
}

export const GlobalSectionsHeader = observer(function _GSH({
  search = '',
  ...props
}: {
  search?: string;
  selection: SelectionStore;
  analytics: AnalyticsService;
  suggestionStore: SingleSuggestionStore;
  library: LibraryCache;
}) {
  const { project } = props.selection;
  const globals = fuzzyTextSearch(
    globalSectionsInProject(project).entries(),
    search,
    ([, { value }]) => value.name,
  );
  if (!globals.length) return null;

  return (
    <Rows spacing={16}>
      <Rows spacing={8}>
        <TextTinyLight color="grey">Global sections</TextTinyLight>
        {globals.map(([id, { value, count }]) => (
          <AddSectionButton
            {...props}
            key={id}
            section={aReferenceSection({ id })}
            size="large"
          >
            <CardButtonContent
              image={globalSectionImage}
              label={value.name}
              locale={project.locale || DEFAULT_LOCALE}
              description={count === 1 ? '1 instance' : `${count} instances`}
            />
          </AddSectionButton>
        ))}
      </Rows>
      <TextTinyLight color="grey">All sections</TextTinyLight>
    </Rows>
  );
});

export function Search({
  navigation,
  route,
}: {
  navigation: SidebarNavigation;
  route: AddRoute;
}) {
  const setSearch = React.useCallback(
    (search: string) => {
      if (search === '') {
        return clearSearch(navigation, route);
      }
      if (route.path === 'add/search') {
        navigation.goto({ ...route, search });
      } else if (route.path === 'add') {
        navigation.goto({
          path: 'add/search',
          search,
          filter: { type: 'sections' },
        });
      } else if (route.path === 'add/page-categories') {
        navigation.goto({
          path: 'add/search',
          search,
          filter: { type: 'pages' },
        });
      } else {
        throw new UnreachableError(route);
      }
    },
    [navigation, route],
  );

  return (
    <>
      <TextSmallLight tag="div">
        <FormInput
          autoFocus
          type="search"
          placeholder="Search"
          value={route.search || ''}
          onChange={setSearch}
          iconStart={IconSearch}
          buttonEnd={
            route.search && (
              <Button
                size="small"
                variant="subtle"
                icon={IconClear}
                onClick={() => clearSearch(navigation, route)}
              />
            )
          }
        />
      </TextSmallLight>
      {route.search && !route.filter.category && (
        <Tabs navigation={navigation} route={route} />
      )}
    </>
  );
}

export function clearSearch(navigation: SidebarNavigation, route: AddRoute) {
  if (route.path !== 'add/search') {
    throw new Error(`can not clear search in ${route.path}`);
  }
  if (route.filter.category) {
    navigation.goto({
      path: 'add/search',
      search: '',
      filter: route.filter,
    });
    return;
  }
  const path = route.filter.type === 'sections' ? 'add' : 'add/page-categories';
  navigation.goto({ path: path });
}

const TAB_OPTIONS = [
  { label: 'Sections', value: 'sections' },
  { label: 'Pages', value: 'pages' },
] as const;

function Tabs({
  navigation,
  route,
}: {
  navigation: SidebarNavigation;
  route: AddRoute;
}) {
  return (
    <ButtonPalette>
      <RadioButtonGroup
        options={TAB_OPTIONS}
        value={
          route.path === 'add/page-categories' ||
          (route.path === 'add/search' && route.filter.type === 'pages')
            ? 'pages'
            : 'sections'
        }
        onChange={() => {
          if (route.path === 'add/search') {
            navigation.goto({
              path: 'add/search',
              search: route.search,
              filter: {
                type: route.filter.type === 'pages' ? 'sections' : 'pages',
                // can not change tabs while category selected
              },
            });
          } else {
            navigation.goto({
              path: route.path === 'add' ? 'add/page-categories' : 'add',
            });
          }
        }}
      />
    </ButtonPalette>
  );
}

export function GotoPageCategoriesRow({
  navigation,
}: {
  navigation: SidebarNavigation;
}) {
  return (
    <Rows spacing={16}>
      <Button
        onClick={() => {
          navigation.goto({ path: 'add/page-categories' });
        }}
        fullWidth
        variant="secondary"
        size="large"
      >
        <CardButtonContent
          label="Pages"
          image={pageCategoriesImage}
          icon={IconChevronRight}
        />
      </Button>
      <div />
    </Rows>
  );
}

export function AddElementButton({
  children,
  element,
  selection,
  suggestionStore,
  size = 'large',
  sidebarNavigation,
  library,
  assistant,
}: {
  children: React.ReactNode;
  element: ElementInstanceState;
  selection: SelectionStore;
  suggestionStore: SingleSuggestionStore;
  size?: 'medium' | 'large';
  sidebarNavigation: SidebarNavigation;
  library: LibraryCache;
  assistant: Assistant;
}) {
  // Preload the component JSON for the element so there's no delay when
  // rendering the suggestion on hover.
  library.getCachedPublicComponentJson(element.source.slug, {
    fetchIfMissing: true,
  });

  const removeMySuggestion = React.useRef<RemoveSingleSuggestion>();
  React.useEffect(() => () => removeMySuggestion.current?.(), []);

  const sel = selection.get();
  if (!sel.section || !sel.element) return null;

  const section = unrefSection(sel.section, selection.project);
  if (!section) return null;

  const rootElement = section.element.current;
  const parentElement =
    rootElement && parentElementWithinRoot(sel.element, rootElement);
  if (!parentElement) return null;

  const tuple = Array.from(parentElement.slotOverrides.entries()).find(
    ([, slot]) => slot.items.includes(sel.element),
  );
  if (!tuple) return null;
  const [slotName, slotOverride] = tuple;

  const comp = library.getCachedPublicComponentJson(
    parentElement.source.slug,
  )?.value;
  if (!comp) return null;

  const slotInfos = findSlotsIn(parentElement.source, comp);
  const slotInfo = slotInfos.find((slotInfo) => slotInfo.name === slotName);
  if (!slotInfo) return null;

  const slotType = library.getCachedSlotType(slotInfo.typeId)?.value;
  if (!slotType) return null;

  const direction = sel.addDirection ?? 'after';

  const suggestion = {
    type: 'add-element',
    page: sel.page || selection.project.homePage,
    slotOverride,
    slotInfo,
    element,
    sref: sel.section,
    index:
      slotOverride.items.indexOf(sel.element) +
      (direction === 'before' ? 0 : 1),
  } satisfies Suggestion;

  const onClick = () => {
    // This case is unlikely, since if max items has been reached, the user
    // shouldn't be able to open the add panel in the first place. But, it could
    // happen the user already had the add panel open, and someone else added
    // something to the slot.
    if (!canAddToSlot(slotOverride, slotType).result) {
      // TODO(george): show a toast?
      return;
    }

    suggestionStore.clear();
    commitSuggestion(suggestion, selection, library);
    const error = getGenerateCopyError(
      suggestion.sref,
      sel.page.state,
      sel.space,
      selection.project.state,
    );
    if (!error) {
      assistant.generateCopy(new Map([[suggestion.sref, undefined]]));
      return;
    }
    sidebarNavigation.goto({ path: 'edit' });
  };

  const onMouseEnter = () => {
    removeMySuggestion.current = suggestionStore.set(suggestion);
  };
  const onMouseLeave = () => {
    // a delay to reduce flickering when mousing between buttons
    removeMySuggestion.current?.({ delayMs: 100 });
  };

  return (
    <Button
      onClick={onClick}
      fullWidth
      variant="secondary"
      size={size}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </Button>
  );
}
